import { useEffect, useRef } from 'react';

/**
 * ScrollProgress — fine ligne terracotta horizontale en haut de viewport qui
 * se remplit au scroll. Pattern porté depuis equipe-buteau adapté Océanne.
 *
 * Phase 27.73 perf optim : refactor useState → useRef + manipulation DOM directe.
 * Avant : setProgress() à chaque scroll frame → React reconciliation 60×/sec.
 * Après : useRef + element.style.transform direct → GPU compositing, zero React render.
 * Pattern équivalent à motion/react useScroll utilisé sur Buteau/Serujan
 * (sans la dep framer-motion qu'Océanne n'a pas).
 */
export function ScrollProgress() {
  const filRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
        const fil = filRef.current;
        if (fil) {
          fil.style.transform = `scaleX(${ratio})`;
        }
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Recompute après mount (atmosphere watercolor / lazy sections changent scrollHeight)
    const timeouts = [500, 1500, 2500].map((d) => window.setTimeout(onScroll, d));
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  return (
    <>
      {/* Rail discret cream-deep — toute la largeur du viewport */}
      <div
        className="fixed inset-x-0 top-0 z-[60] h-[2px] pointer-events-none"
        style={{ backgroundColor: 'oklch(0.62 0.135 38 / 0.08)' }}
        aria-hidden="true"
      />
      {/* Fil terracotta qui se remplit au scroll (transform GPU, scaleX origin-left).
          useRef bypass React render — .style.transform écrit direct sur le GPU layer. */}
      <div
        ref={filRef}
        className="fixed inset-x-0 top-0 z-[60] h-[2px] origin-left pointer-events-none transition-transform duration-150 ease-out motion-reduce:transition-none"
        style={{
          transform: 'scaleX(0)',
          background:
            'linear-gradient(to right, oklch(0.62 0.135 38) 0%, oklch(0.62 0.135 38 / 0.85) 100%)',
          boxShadow: '0 0 8px oklch(0.62 0.135 38 / 0.3)',
        }}
        aria-hidden="true"
      />
    </>
  );
}
